/**
 * New console — appearance and accessibility.
 *
 * Twelve settings in four groups, each stated in terms of what it does to the screen
 * rather than what it is called. "Density" means nothing on its own; "tighter rows
 * fit more, roomier rows are easier to hit" is the actual choice being made.
 *
 * The panel covers the screen it is changing, so it carries a miniature of that
 * screen at the top — a heading, a badge of each status, a button, two table rows.
 * Every setting here visibly moves something in it, which is the difference between
 * choosing a setting and guessing at one.
 *
 * Everything applies immediately and there is no Save: these are settings someone
 * adjusts by looking at the result. Nothing here is stored on the account — a wall
 * display in a clinic and the same person's laptop want different answers, and there
 * is no endpoint for them anyway.
 */
import { ACCENTS, DEFAULTS, SCALE, clampScale } from '../prefs';
import { Panel } from './Overlay';
import { Badge, Button, Note, HoverTip } from './Primitives';
import { SetRow, Switch, Seg, Group } from './SettingRow';
import { IconSun, IconMoon, IconAccess } from './Icons';

/* The console in miniature. Deliberately built from the real classes rather than a
   drawing of them, so it cannot show one thing while the console does another. */
const Preview = () => (
  <div className="nt-preview" aria-hidden="true">
    <div className="nt-preview-head">
      <span className="t">Clinic overview</span>
      <span className="nt-spacer" />
      <Badge tone="good">On track</Badge>
    </div>
    <div className="nt-preview-body">
      <div className="nt-preview-row">
        <Button size="sm" variant="primary">Primary</Button>
        <Button size="sm">Secondary</Button>
        <Badge tone="warn">Warning</Badge>
        <Badge tone="crit">Missed</Badge>
        <Badge tone="info">Info</Badge>
      </div>
      <table className="nt-preview-tbl">
        <thead>
          <tr><th>Clinic</th><th style={{ textAlign: 'right' }}>Leads</th><th style={{ textAlign: 'right' }}>Missed</th></tr>
        </thead>
        <tbody>
          <tr><td>Bellevue Chiropractic</td><td className="n">128</td><td className="n">4</td></tr>
          <tr><td>Tacoma Health Connection</td><td className="n">96</td><td className="n">22</td></tr>
          <tr><td>Active Family Wellness</td><td className="n">74</td><td className="n">3</td></tr>
        </tbody>
      </table>
    </div>
  </div>
);

const A11yPanel = ({ prefs, setPrefs, onClose }) => {
  /* Functional update, not { ...prefs, ... }. Two settings changed in the same tick
     both read the same captured prefs and the second silently discards the first —
     which is exactly what happens when someone adjusts size and density together. */
  const set = (key) => (value) => setPrefs((p) => ({ ...p, [key]: value }));

  return (
    <Panel
      title="Appearance and accessibility"
      onClose={onClose}
      footer={(
        <>
          <Button onClick={() => setPrefs((p) => ({ ...DEFAULTS, railMini: p.railMini }))}>
            Reset to defaults
          </Button>
          <span className="nt-spacer" />
          <Button variant="primary" onClick={onClose}>Done</Button>
        </>
      )}
    >
      <Preview />

      <Group title="Theme">
        <SetRow title="Light or dark" detail="System follows the setting on this device and changes with it.">
          <Seg
            label="Light or dark"
            value={prefs.themeMode}
            onChange={set('themeMode')}
            options={[
              { value: 'system', label: 'System', icon: <IconAccess />, hint: 'Follow this device' },
              { value: 'light', label: 'Light', icon: <IconSun /> },
              { value: 'dark', label: 'Dark', icon: <IconMoon /> }
            ]}
          />
        </SetRow>

        <SetRow title="Accent colour" detail="Buttons, links and the active rail item. Status colours never change — a red badge stays red.">
          <div className="nt-swatches">
            {ACCENTS.map((a) => (
              <HoverTip key={a.value} text={a.label}>
                <button
                  type="button"
                  aria-label={a.label}
                  aria-pressed={prefs.accent === a.value}
                  className={`nt-swatch${prefs.accent === a.value ? ' on' : ''}`}
                  style={{ background: a.swatch }}
                  onClick={() => set('accent')(a.value)}
                />
              </HoverTip>
            ))}
          </div>
        </SetRow>

        <SetRow title="Corners" detail="Rounds or squares every card, field and button together.">
          <Seg
            label="Corners"
            value={prefs.corners}
            onChange={set('corners')}
            options={[
              { value: 'rounded', label: 'Rounded' },
              { value: 'soft', label: 'Soft' },
              { value: 'square', label: 'Square' }
            ]}
          />
        </SetRow>

        <SetRow title="High contrast" detail="Stronger borders and darker text, for glare or low vision.">
          <Switch
            label="High contrast"
            on={prefs.contrast === 'high'}
            onChange={(v) => set('contrast')(v ? 'high' : 'normal')}
          />
        </SetRow>
      </Group>

      <Group title="Text">
        <SetRow title="Text size" detail="Scales every label, number and table in the console.">
          <div className="nt-slider">
            <input
              type="range"
              min={SCALE.min}
              max={SCALE.max}
              step={SCALE.step}
              value={prefs.fontScale}
              onChange={(e) => set('fontScale')(clampScale(e.target.value))}
              aria-label="Text size"
            />
            <span className="v">{Math.round(Number(prefs.fontScale) * 100)}%</span>
          </div>
        </SetRow>

        <SetRow title="Readable typeface" detail="Atkinson Hyperlegible where it is installed, and a wider-spaced stack where it is not.">
          <Switch
            label="Readable typeface"
            on={prefs.typeface === 'legible'}
            onChange={(v) => set('typeface')(v ? 'legible' : 'system')}
          />
        </SetRow>
      </Group>

      <Group title="Layout">
        <SetRow title="Density" detail="Tighter rows fit more on screen; roomier rows are easier to hit.">
          <Seg
            label="Density"
            value={prefs.density}
            onChange={set('density')}
            options={[
              { value: 'compact', label: 'Compact' },
              { value: 'cosy', label: 'Cosy' },
              { value: 'roomy', label: 'Roomy' }
            ]}
          />
        </SetRow>

        <SetRow title="Striped rows" detail="Shades alternate table rows — the easiest way to hold one clinic across a wide table.">
          <Switch
            label="Striped rows"
            on={prefs.zebra === 'on'}
            onChange={(v) => set('zebra')(v ? 'on' : 'off')}
          />
        </SetRow>
      </Group>

      <Group title="Clarity">
        <SetRow title="Underline links" detail="Marks anything clickable with more than colour alone.">
          <Switch
            label="Underline links"
            on={prefs.underline === 'on'}
            onChange={(v) => set('underline')(v ? 'on' : 'off')}
          />
        </SetRow>

        <SetRow title="Status shapes" detail="Adds a tick, arrow or cross to each badge, so status does not depend on telling red from green.">
          <Switch
            label="Status shapes"
            on={prefs.statusShapes === 'on'}
            onChange={(v) => set('statusShapes')(v ? 'on' : 'off')}
          />
        </SetRow>

        <SetRow title="Always show the focus ring" detail="Normally it appears only when you navigate by keyboard.">
          <Switch
            label="Always show the focus ring"
            on={prefs.focusRing === 'always'}
            onChange={(v) => set('focusRing')(v ? 'always' : 'auto')}
          />
        </SetRow>

        <SetRow title="Reduce motion" detail="Turns off slide, fade and the loading shimmer.">
          <Switch
            label="Reduce motion"
            on={prefs.motion === 'reduced'}
            onChange={(v) => set('motion')(v ? 'reduced' : 'full')}
          />
        </SetRow>
      </Group>

      <div className="nt-mt-16">
        <Note>
          These are stored in this browser, not on your account — the screen you sit at is
          what they describe. Your system&apos;s own &ldquo;reduce motion&rdquo; setting is
          honoured whether or not the switch above is on.
        </Note>
      </div>
    </Panel>
  );
};

export default A11yPanel;
